/**
 * Meldformulier onveilige situatie (Veiligheid-module — eigen lichtgewicht
 * model). Een monteur meldt vanaf de portal een onveilige situatie; de
 * backoffice (TEAMLEIDER/HR) volgt op via status + opvolgingsnotitie.
 *
 * Gedeeld tussen API (DTO-output + Zod-input) en de portal/backoffice-UI.
 * Gate-key voor alle endpoints/pagina's is de BESTAANDE module-key `VEILIGHEID`.
 */

import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Enums (string-unions, gelijk aan Prisma-enums) ──────────────────────────

export const MELDING_ONVEILIG_PRIORITEIT = ['LAAG', 'NORMAAL', 'HOOG'] as const
export type MeldingOnveiligPrioriteit = (typeof MELDING_ONVEILIG_PRIORITEIT)[number]

export const MELDING_ONVEILIG_STATUS = [
  'INGEDIEND',
  'IN_BEHANDELING',
  'AFGEHANDELD',
  'GESLOTEN',
] as const
export type MeldingOnveiligStatus = (typeof MELDING_ONVEILIG_STATUS)[number]

// Terminale statussen — bij overgang hiernaartoe wordt afgehandeldDoor/-Op gezet.
export const MELDING_ONVEILIG_TERMINALE_STATUS: ReadonlyArray<MeldingOnveiligStatus> = [
  'AFGEHANDELD',
  'GESLOTEN',
]

// ─── Labels (NL-talig voor UI) ───────────────────────────────────────────────

export const MELDING_ONVEILIG_PRIORITEIT_LABELS: Record<MeldingOnveiligPrioriteit, string> = {
  LAAG: 'Laag',
  NORMAAL: 'Normaal',
  HOOG: 'Hoog',
}

export const MELDING_ONVEILIG_STATUS_LABELS: Record<MeldingOnveiligStatus, string> = {
  INGEDIEND: 'Ingediend',
  IN_BEHANDELING: 'In behandeling',
  AFGEHANDELD: 'Afgehandeld',
  GESLOTEN: 'Gesloten',
}

// ─── DTO ─────────────────────────────────────────────────────────────────────

export interface MeldingOnveiligeSituatieDTO {
  id: string
  /** Employee-id van de melder. */
  employeeId: string
  /** Naam-resolved melder (firstName + lastName), of null. */
  melderNaam: string | null
  omschrijving: string
  locatie: string | null
  clientId: string | null
  /** Naam van de gekoppelde klant/relatie, of null. */
  clientNaam: string | null
  prioriteit: MeldingOnveiligPrioriteit
  status: MeldingOnveiligStatus
  /** Opslag-keys van geüploade foto's (intern); UI streamt via de proxy. */
  fotoPaths: string[]
  ingediendOp: string
  opvolgingNotitie: string | null
  afgehandeldDoorId: string | null
  /** Naam-resolved afhandelaar, of null. */
  afgehandeldDoorNaam: string | null
  afgehandeldOp: string | null
  createdAt: string
  updatedAt: string
}

// ─── Zod request-body schemas + input types ──────────────────────────────────

export const createMeldingOnveiligeSituatieSchema = z.object({
  omschrijving: z.string().trim().min(5).max(4000),
  locatie: z.string().trim().max(500).optional(),
  // Bare String-kolom (legacy UUID-format mogelijk) → idParam i.p.v. cuid().
  clientId: idParam().optional(),
  prioriteit: z.enum(MELDING_ONVEILIG_PRIORITEIT).optional(),
})
export type CreateMeldingOnveiligeSituatieInput = z.infer<
  typeof createMeldingOnveiligeSituatieSchema
>

export const updateMeldingOnveiligeSituatieSchema = z
  .object({
    status: z.enum(MELDING_ONVEILIG_STATUS).optional(),
    opvolgingNotitie: z.string().trim().max(4000).nullable().optional(),
  })
  .refine((v) => v.status !== undefined || v.opvolgingNotitie !== undefined, {
    message: 'Geef minimaal een status of opvolgingsnotitie mee',
  })
export type UpdateMeldingOnveiligeSituatieInput = z.infer<
  typeof updateMeldingOnveiligeSituatieSchema
>

export const listMeldingOnveiligeSituatieQuerySchema = z.object({
  status: z.enum(MELDING_ONVEILIG_STATUS).optional(),
  prioriteit: z.enum(MELDING_ONVEILIG_PRIORITEIT).optional(),
})
export type ListMeldingOnveiligeSituatieQuery = z.infer<
  typeof listMeldingOnveiligeSituatieQuerySchema
>
